import React, { useState } from 'react';
import { View, Image, TouchableOpacity, FlatList } from 'react-native';
import Header from '../../../components/Header';
import DropdownButton from '../../../components/DropdownButton';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Card from '../../../components/Card';
import MyAppText from '../../../components/MyAppText';
import Divider from '../../../components/Divider';
import moment from 'moment';
import Badge, { BadgeColor } from '../../../components/Badge';
import { ScrollView } from 'react-native-web-hover';
import FormInput from '../../../components/FormInput';
import { useForm } from 'react-hook-form';
import CustomButton from '../../../components/CustomButton';
import { useQuery } from '@tanstack/react-query';
import { crmService } from '../../../services/central-api/crm';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { removeDeletedDateFromSerialNo } from '../../../utils/removeDeletedDateFromSerialNo';
import Container from '../../../components/Container';
import { useCurrentApp } from '../../../context/CurrentApp';
import { openInNewTab } from '../../../services/navigation-assistant';
import { RootNavigation } from '../../../Routes';
import ReviewModal from './ReviewModal';
import AlertModal from '../../../components/AlertModal';
import FontWeight from '../../../styles/fontWeights';
import { centralAPI } from '../../../services/central-api';
import { WindowInformation } from '../../../services/window-information';
import { TranslateRich } from '../../../components/TranslateRich';

function Report(props: {
    type: 'report'; value: MaintenanceReport;
} | {
    type: 'status'; value: MaintenanceRequestStatusHistory;
} | {
    type: 'assign'; value: { createdAt: string; responsibleUser: MaintenanceRequestResponsibleUser; };
}) {
    const { styles } = useStyles(stylesheet);

    if (props.type === 'report') {
        return <View style={{ gap: 8 }}>
            <View style={styles.reportHeader}>
                <Image style={styles.userPin} source={require('../../../../assets/SentinelX_pin.png')} />
                <View style={{ flexGrow: 1, justifyContent: 'center', gap: 4 }}>
                    <MyAppText style={styles.reportUser}>{props.value.user?.name || translate('systemGenerated')}</MyAppText>
                    {/* TODO: adicionar categoria aqui?*/}
                    {/* <Badge mode='ROUNDED' size={'small'} backgroundColor={props.value.reason.color} textColor={getReasonTagTextColor(props.value.reason.color)}>{props.value.reason.name}</Badge> */}
                </View>
                <MyAppText style={styles.reportDate}>{moment(props.value.createdAt).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
            </View>
            <MyAppText style={styles.reportDetail}>{props.value.observation}</MyAppText>
        </View>;
    } else if (props.type === 'status') {
        return <View style={{ gap: 8 }}>
            <View style={[styles.reportHeader, { flex: 1 }]}>
                <View style={{ flex: 1 }}>
                    <MyAppText style={styles.statusText}>
                        <TranslateRich
                            i18nKey={'maintenanceRequestChangedTo'}
                            values={{
                                status: <strong>{translate(props.value.status)}</strong>,
                            }}
                        />
                    </MyAppText>
                </View>
                <MyAppText style={styles.reportDate}>{moment(props.value.createdAt).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
            </View>
        </View>;
    } else {
        return <View style={{ gap: 8 }}>
            <View style={[styles.reportHeader, { flex: 1 }]}>
                <View style={{ flex: 1 }}>
                    <MyAppText style={styles.statusText}>
                        <TranslateRich
                            i18nKey={'maintenanceRequestAssignedBy'}
                            values={{
                                userName: <strong>{props.value.responsibleUser.name}</strong>,
                            }}
                        />
                    </MyAppText>
                </View>
                <MyAppText style={styles.reportDate}>{moment(props.value.createdAt).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
            </View>
        </View>;
    }


}

export const maintenanceRequestBadgeColors: { [key in CameraMaintenanceStatus]: BadgeColor; } = {
    pending: 'orange',
    concluded: 'green',
    in_progress: 'blue',
    review: 'purple',
    under_analysis: 'teal',
};

export interface MaintenanceReportForm {
    observation: string;
}

function SideBarItem(props: { title: string; content: string; direction: 'row' | 'column'; contentTextColor?: string; }) {
    const { styles } = useStyles(stylesheet);

    return <View style={{ flexDirection: props.direction, gap: props.direction === 'row' ? 8 : 0 }}>
        <MyAppText style={styles.sideBarItemTitle}>{props.title}:</MyAppText>
        <MyAppText style={[styles.sideBarItemContent, props.contentTextColor ? { color: props.contentTextColor } : undefined]}>{props.content}</MyAppText>
    </View>;
}

export default function MaintenanceRequestComponent({ navigation, route, page }: {
    navigation: RootNavigation;
    route: {
        params: {
            id: number;
        };
    };
    page: 'maintenance-requests' | 'kanban-maintenance-requests' | 'own-maintenance-requests';
}) {
    const { styles, theme } = useStyles(stylesheet);
    const { isParentApp, isAgentApp } = useCurrentApp();
    const { user: authenticatedUser } = useAuth();
    const [isConfirmObservationWithoutAckModalVisible, setIsConfirmObservationWithoutAckModalVisible] = useState(false);
    const [isConfirmConclusionModalVisible, setIsConfirmConclusionModalVisible] = useState(false);
    const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
    const { isMobile } = WindowInformation();

    const defaultEmptyValues: MaintenanceReportForm = {
        observation: '',
    };

    const form = useForm<MaintenanceReportForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    function getDuration(paramStart: string, paramFinish: string | null) {
        const finish = paramFinish ? moment(paramFinish) : moment();
        const start = moment(paramStart);

        const totalSeconds = Math.max(0, finish.diff(start, 'seconds'));

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const pad = (n: number) => String(n).padStart(2, '0');

        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    const queryResult = useQuery({
        queryKey: ['getMaintenanceRequestDetail', route.params.id],
        queryFn: () => page === 'own-maintenance-requests' ? centralAPI.getOwnMaintenanceRequest(route.params.id) : crmService.getMaintenanceRequest(route.params.id),
        enabled: !!route.params.id && !!authenticatedUser,
    });

    const { data, refetch, isLoading } = queryResult;

    async function assignMaintenanceRequest(maintenanceRequestId: number) {
        try {
            await crmService.ackMaintenanceRequest(maintenanceRequestId);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function sendReport() {
        try {
            const values = form.getValues();

            if (!form.formState.isValid) {
                return;
            }

            await crmService.updateMaintenanceRequest(route.params.id, {
                observation: values.observation,
            });

            refetch();
            form.reset(defaultEmptyValues);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    async function sendToInProgress() {
        try {
            await crmService.sendMaintenanceRequestToInProgress(route.params.id);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function sendToReview() {
        try {
            await crmService.sendMaintenanceRequestToReview(route.params.id);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function rejectRequest(reason: string) {
        try {
            if (page === 'own-maintenance-requests') {
                await centralAPI.rejectOwnMaintenanceRequest(route.params.id, { reason: `${translate('rejected')}: ${reason}` });
            } else {
                await crmService.rejectMaintenanceRequest(route.params.id, { reason: `${translate('rejected')}: ${reason}` });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setIsReviewModalVisible(false);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    async function concludeRequest() {
        try {
            if (page === 'own-maintenance-requests') {
                await centralAPI.concludeOwnMaintenanceRequest(route.params.id);
            } else {
                await crmService.concludeMaintenanceRequest(route.params.id);
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setIsReviewModalVisible(false);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    function renderItem({ item }: {
        item: {
            type: 'report'; value: MaintenanceReport;
        } | {
            type: 'status'; value: MaintenanceRequestStatusHistory;
        } | {
            type: 'assign'; value: { createdAt: string; responsibleUser: MaintenanceRequestResponsibleUser; };
        };
    }) {
        return <Report {...item} />;
    }

    if (isLoading || !data) {
        return <></>;
    }

    function getStatus(data: MaintenanceRequest): CameraMaintenanceStatus {

        const finish = data.finish;
        const status = (data.statusHistory || []).at(-1)?.status;

        if (finish) {
            return 'concluded';
        }

        if (status) {
            return status;
        }

        return 'pending';
    }

    function getBadgeStatus(status: CameraMaintenanceStatus) {
        const color = maintenanceRequestBadgeColors[status];
        return <Badge mode='SQUARE' color={color}>{translate(status)}</Badge>;
    }

    return <Container>
        <Header
            goBackAction={() => {
                if (navigation.canGoBack()) {
                    return navigation.goBack();
                }
                if (page === 'kanban-maintenance-requests') {
                    return navigation.navigate('CRM', {
                        screen: 'KanbanMaintenanceRequests',
                        params: {
                            companyId: data.camera.companyId,
                        }
                    });
                }
                if (page === 'maintenance-requests') {
                    return navigation.navigate('CRM', {
                        screen: 'MaintenanceRequests',
                        params: {
                            page: 1,
                            limit: 25,
                            orderBy: 'start',
                            orderDirection: 'desc',
                            onlyOffline: false,
                            onlyClosed: false,
                        }
                    });
                }
                if (page === 'own-maintenance-requests') {
                    return navigation.navigate('User', {
                        screen: 'OwnMaintenanceRequests',
                        params: {
                            page: 1,
                            limit: 25,
                            orderBy: 'start',
                            orderDirection: 'desc',
                            onlyOffline: false,
                            onlyClosed: false,
                        }
                    });
                }
            }}
            title={`${translate('maintenanceRequest')}: #${data.id}`}
            actions={<>
                <DropdownButton
                    viewStyle={{ height: '100%' }}
                    isButton
                    actions={[{
                        content: translate('openCameraConfigurationOnNewTab'),
                        onPress: () => {
                            openInNewTab('CRM', { screen: 'ManagementDetails', params: { id: data.camera.id } });
                        },
                        omit: isAgentApp,
                    }, {
                        content: translate('watchCameraOnNewTab'),
                        onPress: () => {
                            openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id: data.camera.id } });
                        },
                        omit: isAgentApp,
                    }, {
                        content: translate('sendToInProgress'),
                        onPress: () => {
                            sendToInProgress();
                        },
                        omit: !isParentApp || getStatus(data) !== 'under_analysis' || !data.responsibleUserId,
                    }, {
                        content: translate('sendToReview'),
                        onPress: () => {
                            sendToReview();
                        },
                        omit: !isParentApp || !['under_analysis', 'in_progress'].includes(getStatus(data)) || !data.responsibleUserId,
                    }]}
                    buttonIcon={faChevronDown}
                    buttonType={'secondary'}
                    buttonLabel='actions'
                    offsetX={-118}
                    offsetY={45}
                    size={320}
                />
                {!isParentApp && !data.finish && getStatus(data) !== 'review' && <CustomButton
                    onPress={async () => {
                        setIsConfirmConclusionModalVisible(true);
                    }}
                    text={translate('conclude')}
                />}
                {!isParentApp && !data.finish && getStatus(data) === 'review' && <CustomButton
                    onPress={async () => {
                        setIsReviewModalVisible(true);
                    }}
                    text={translate('toReview')}
                />}
            </>}
        />
        <View style={styles.container}>
            <Card style={{ gap: 16 }}>
                <View style={styles.cardTitleContainer}>
                    <MyAppText style={styles.cardTitle}>{translate('historic')}</MyAppText>
                </View>
                <View style={styles.reports}>
                    <FlatList
                        style={{
                            padding: 16,
                        }}
                        data={[
                            ...(data.reports || []).map(r => ({ type: 'report' as const, value: r })),
                            ...(data.statusHistory || []).map(s => ({ type: 'status' as const, value: s })),
                            ...(() => {
                                const defaultStatus: { type: 'status'; value: MaintenanceRequestStatusHistory; }[] = [{
                                    type: 'status',
                                    value: {
                                        createdAt: data.start,
                                        status: 'pending',
                                    },
                                }];

                                if (data.finish) {
                                    defaultStatus.push({
                                        type: 'status',
                                        value: {
                                            createdAt: data.finish,
                                            status: 'concluded',
                                        },
                                    });
                                }

                                return defaultStatus;
                            })(),
                            ...(() => {
                                const assign: { type: 'assign'; value: { createdAt: string; responsibleUser: MaintenanceRequestResponsibleUser; }; }[] = [];

                                if (data.assignedAt && data.responsibleUser) {
                                    assign.push({
                                        type: 'assign',
                                        value: {
                                            createdAt: data.assignedAt,
                                            responsibleUser: data.responsibleUser,
                                        }
                                    });
                                }

                                return assign;
                            })(),
                        ].sort((a, b) => {
                            if (a.value.createdAt === b.value.createdAt) {
                                if (a.type === 'status') {
                                    return -1;
                                }
                                return 1;
                            }
                            return moment(a.value.createdAt).isBefore(moment(b.value.createdAt)) ? -1 : 1;
                        })}
                        renderItem={renderItem}
                        ItemSeparatorComponent={() => <View style={{ paddingVertical: 16 }}><Divider color={theme.colors.backgrounds['BG-1']} /></View>}
                    />
                </View>
                {isParentApp && !data.finish && <>
                    <Divider />
                    <View style={{ gap: 8 }}>
                        <View style={{ flexDirection: 'row', gap: 8 }}>
                            <View style={{ flex: 1 }}>
                                <FormInput
                                    form={form}
                                    fieldName='observation'
                                    placeholder={translate('addAnObservation')}
                                    multiline
                                    inputStyle={{ height: 110 }}
                                    required
                                    maxLength={65535}
                                />
                            </View>
                            <View style={{ width: 110, justifyContent: 'flex-end' }}>
                                <CustomButton
                                    onPress={async () => {
                                        if (data.responsibleUserId === authenticatedUser?.id) {
                                            await sendReport();
                                        } else {
                                            setIsConfirmObservationWithoutAckModalVisible(true);
                                        }
                                    }}
                                    text={translate('send')}
                                    disabled={!form.formState.isValid}
                                />
                            </View>
                        </View>
                    </View>
                </>}
            </Card>
            {!isMobile &&
                <>
                    <Divider color={theme.colors.neutral['300']} orientation='vertical' />
                    <Card style={{ maxWidth: 382, padding: 24, gap: 16 }}>
                        <View style={styles.cardTitleContainer}>
                            <MyAppText style={styles.cardTitle}>{translate('general_info')}</MyAppText>
                            {getBadgeStatus(getStatus(data))}
                        </View>
                        <ScrollView contentContainerStyle={{ gap: 16 }}>
                            <View style={{ gap: 8 }}>
                                <MyAppText style={styles.sideBarTitle}>{translate('responsible')}</MyAppText>
                                {data.responsibleUser ? <View style={{ flexDirection: 'row', gap: 8 }}>
                                    <MyAppText style={styles.sideBarItemContent}>{data.responsibleUser.warName || data.responsibleUser.name}</MyAppText>
                                </View> : <View style={{ flexDirection: 'row', gap: 8 }}>
                                    <MyAppText style={styles.sideBarItemContent}>{translate('noOne')}</MyAppText>
                                    {isParentApp && !data.finish && <TouchableOpacity onPress={() => assignMaintenanceRequest(route.params.id)}>
                                        <MyAppText style={styles.ackText}>{translate('assumeMaintenanceRequest')}</MyAppText>
                                    </TouchableOpacity>
                                    }
                                </View>}
                            </View>
                            <Divider color={theme.colors.backgrounds['BG-1']} />
                            <View style={{ gap: 8 }}>
                                <MyAppText style={styles.sideBarTitle}>{translate('cameraInfoLabel')}</MyAppText>
                                <View style={{ gap: 4 }}>
                                    <SideBarItem title={translate('Id')} content={data.camera.id} direction='row' />
                                    <SideBarItem title={translate('title')} content={data.camera.title} direction='column' />
                                    <SideBarItem title={translate('serialNo')} content={removeDeletedDateFromSerialNo(data.camera.serialNo ?? '')} direction='row' />
                                    <SideBarItem title={translate('address')} content={data.camera.address} direction='column' />
                                    <SideBarItem title={translate('stolen_equipment')} content={data.camera.stolen ? translate('yes') : translate('no')} direction='row' />
                                    <SideBarItem
                                        title={translate('currentlyOnline')}
                                        contentTextColor={data.camera.isConnected ? theme.colors.online.fontColor : theme.colors.offline.fontColor}
                                        content={data.camera.isConnected ? translate('online') : translate('offline')} direction='row'
                                    />
                                    <SideBarItem title={translate('disabled')} content={data.camera.disabledAt !== null ? translate('yes') : translate('no')} direction='row' />
                                    <SideBarItem title={translate('femaleDeleted')} content={data.camera.deletedAt !== null ? translate('yes') : translate('no')} direction='row' />
                                </View>
                            </View>
                            <Divider color={theme.colors.backgrounds['BG-1']} />
                            <View style={{ gap: 8 }}>
                                <MyAppText style={styles.sideBarTitle}>{translate('maintenanceRequestData')}</MyAppText>
                                <View style={{ gap: 4 }}>
                                    {data.priority && <Badge
                                        color={'red'}
                                        mode={'SQUARE'}
                                    >{translate('critical')}</Badge>}
                                    {data.requesterUser &&
                                        <SideBarItem title={translate('requester')} content={`${data.requesterUser.registry} - ${data.requesterUser.warName || data.requesterUser.name}`} direction='column' />
                                    }
                                    <SideBarItem title={translate('maintenanceRequestDateTime')} content={moment(data.start).format('DD/MM/YYYY HH:mm:ss')} direction='column' />
                                    <SideBarItem title={translate('duration')} content={getDuration(data.start, data.finish)} direction='column' />
                                    {data.finish && <SideBarItem title={translate('conclusionDateTime')} content={moment(data.finish).format('DD/MM/YYYY HH:mm:ss')} direction='column' />}
                                </View>
                            </View>
                        </ScrollView>
                    </Card>
                </>
            }
        </View>
        <AlertModal
            isModalVisible={isConfirmObservationWithoutAckModalVisible}
            onSubmit={sendReport}
            setModalVisible={setIsConfirmObservationWithoutAckModalVisible}
            title={translate('confirm')}
            type='warning'
            description={translate('youAreNotResponsibleForTheMaintenanceRequestObservation')}
        />
        <AlertModal
            isModalVisible={isConfirmConclusionModalVisible}
            onSubmit={concludeRequest}
            setModalVisible={setIsConfirmConclusionModalVisible}
            description={translate('confirmMaintenanceRequestConclusion')}
            type='warning'
            title={translate('confirm')}
        />
        <ReviewModal
            isModalVisible={isReviewModalVisible}
            setModalVisible={setIsReviewModalVisible}
            onReject={rejectRequest}
            onConfirm={concludeRequest}
        />
    </Container>;
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        flex: 1,
        flexDirection: 'row',
        gap: 16,
    },
    reports: {
        gap: 32,
        flex: 1,
        borderColor: theme.colors.backgrounds['BG-1'],
        borderWidth: 1,
        borderRadius: 4,
    },
    cardTitle: {
        fontSize: 16,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.neutral[500],
    },
    cardTitleContainer: {
        height: 24,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reportHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 8,
        alignItems: 'center',
    },
    reportDate: {
        fontSize: 12,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.neutral[400],
    },
    reportUser: {
        fontSize: 14,
        fontWeight: FontWeight.semiBold,
        color: theme.colors.neutral[500],
    },
    reportDetail: {
        fontSize: 12,
        color: theme.colors.neutral[400],
    },
    sideBarTitle: {
        fontSize: 14,
        fontWeight: FontWeight.semiBold,
    },
    statusText: {
        fontSize: 12,
    },
    ackText: {
        fontSize: 14,
        color: theme.colors.primary[500],
        textDecorationLine: 'underline',
        textDecorationColor: theme.colors.primary[500],
    },
    sideBarItemTitle: {
        fontSize: 14,
        color: theme.colors.neutral[500],
        fontWeight: FontWeight.semiBold,
    },
    sideBarItemContent: {
        fontSize: 14,
        color: theme.colors.neutral[400],
    },
    userPin: {
        width: 40,
        height: 40,
        borderRadius: 50,
        aspectRatio: 1,
        borderWidth: 1,
        borderColor: theme.colors.dataTable.border,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#FFF'
    }
}));
